import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import PageViewTracker from "@/components/PageViewTracker";
import SpamDebugSummary from "@/components/SpamDebugSummary";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { useLocation, useNavigate } from "@/lib/router-compat";
import { globalSchemas } from "@/lib/seo";
import appCss from "../styles.css?url";

// ── Non-provider shell components ported from the old src/App.tsx ──

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function TrailingSlashRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname !== "/" && location.pathname.endsWith("/")) {
      navigate(location.pathname.slice(0, -1) + location.search + location.hash, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);
  return null;
}

// Signal for pre-render / SSG tools that the page content is ready
function RenderComplete() {
  useEffect(() => {
    document.dispatchEvent(new Event("render-complete"));
    // First-touch attribution capture (UTM / gclid / fbclid / referrer / landing page)
    import("@/lib/attribution").then((m) => m.captureAttribution()).catch(() => {});
  }, []);
  return null;
}

// Ported from index.html: www → apex canonical-host redirect.
const WWW_REDIRECT_SCRIPT = `(function () {
  var h = location.hostname;
  if (h === 'www.hadleyenergysolutions.com') {
    location.replace('https://hadleyenergysolutions.com' + location.pathname + location.search + location.hash);
  }
})();`;

// Ported from index.html: deferred GTM + GA4 loader (idle / first interaction)
// so analytics don't block LCP / TBT on mobile. dataLayer + gtag stubs stay
// synchronous so inline gtag()/dataLayer.push() calls queue correctly.
const ANALYTICS_LOADER_SCRIPT = `window.dataLayer = window.dataLayer || [];
window.gtag = function(){ window.dataLayer.push(arguments); };
gtag('js', new Date());
gtag('config', 'G-5TKR4D0M5X', { send_page_view: false });

(function(){
  var loaded = false;
  function loadAnalytics(){
    if (loaded) return;
    loaded = true;

    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-5D5WMTJ5');

    var ga = document.createElement('script');
    ga.async = true;
    ga.src = 'https://www.googletagmanager.com/gtag/js?id=G-5TKR4D0M5X';
    document.head.appendChild(ga);

    ['scroll','keydown','touchstart','mousemove','click'].forEach(function(ev){
      window.removeEventListener(ev, loadAnalytics, { passive: true });
    });
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadAnalytics, { timeout: 4000 });
  } else {
    window.addEventListener('load', function(){ setTimeout(loadAnalytics, 2000); });
  }
  ['scroll','keydown','touchstart','mousemove','click'].forEach(function(ev){
    window.addEventListener(ev, loadAnalytics, { passive: true, once: true });
  });
})();`;

// Ahrefs Web Analytics: injected at runtime to avoid a hydration-mismatching
// src script in the SSR head. Inline per the same pattern as the GA4/GTM loader.
const AHREFS_LOADER = `(function(){var s=document.createElement('script');s.src='https://analytics.ahrefs.com/analytics.js';s.async=true;s.setAttribute('data-key','TiEvGDUVIoiJBiPywyV3MA');document.head.appendChild(s);})();`;

const CSP_CONTENT =
  "default-src 'self'; base-uri 'self'; form-action 'self' https://api.web3forms.com; object-src 'none'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://*.supabase.co https://analytics.ahrefs.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; style-src-elem 'self' 'unsafe-inline' https://fonts.googleapis.com; img-src 'self' data: blob: https://www.google-analytics.com https://www.googletagmanager.com https://*.supabase.co https://*.supabase.in https://images.unsplash.com https://lh3.googleusercontent.com https://maps.gstatic.com https://maps.googleapis.com; font-src 'self' data: https://fonts.gstatic.com; connect-src 'self' https://www.google.com https://www.google-analytics.com https://*.google-analytics.com https://*.analytics.google.com https://www.googletagmanager.com https://*.supabase.co https://*.supabase.com wss://*.supabase.co https://api.firecrawl.dev https://api.web3forms.com https://analytics.ahrefs.com; frame-src 'self' https://www.google.com https://maps.google.com https://www.google.com/maps https://www.googletagmanager.com https://td.doubleclick.net; media-src 'self' data: blob:; worker-src 'self' blob:; manifest-src 'self'; upgrade-insecure-requests";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700&family=Barlow:wght@700;800&family=IBM+Plex+Sans:wght@400;500;600&display=swap";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "author", content: "Hadley Energy Solutions" },
      { name: "robots", content: "noindex, nofollow" },
      { httpEquiv: "Content-Security-Policy", content: CSP_CONTENT },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      // 404-safe fallbacks. Every real content route overrides these in its own head().
      { title: "Page Not Found | Hadley Energy Solutions" },
      { name: "description", content: "The page you're looking for doesn't exist. Return to Hadley Energy Solutions for electrical, HVAC, and energy services in Louisville, KY." },
      { property: "og:site_name", content: "Hadley Energy Solutions" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Page Not Found | Hadley Energy Solutions" },
      { property: "og:description", content: "The page you're looking for doesn't exist. Return to Hadley Energy Solutions for electrical, HVAC, and energy services in Louisville, KY." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Page Not Found | Hadley Energy Solutions" },
      { name: "twitter:description", content: "The page you're looking for doesn't exist. Return to Hadley Energy Solutions for electrical, HVAC, and energy services in Louisville, KY." },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "preconnect", href: "https://www.google-analytics.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      { rel: "stylesheet", href: FONTS_HREF },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon-64.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    ],
    scripts: [
      { children: WWW_REDIRECT_SCRIPT },
      { children: ANALYTICS_LOADER_SCRIPT },
      // Inline loader (not a src entry): a src script in the SSR head
      // hydration-mismatches against React 19's hoistable-script handling.
      { children: AHREFS_LOADER },

      // Sitewide Organization + LocalBusiness JSON-LD, server-rendered once per
      // page. Page-level schemas skip these @ids (see src/lib/route-head.ts).
      ...globalSchemas().map((schema) => ({
        type: "application/ld+json",
        children: JSON.stringify(schema),
      })),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) — ported from index.html */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-5D5WMTJ5"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <TrailingSlashRedirect />
        <RenderComplete />
        <PageViewTracker />
        <SpamDebugSummary />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  console.error(error);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
      <div className="max-w-md px-6 text-center">
        <h1 className="mb-3 font-heading text-2xl font-bold">This page didn't load</h1>
        <p className="mb-6 font-body text-muted-foreground">
          Something went wrong on our end. Try again or head back home.
        </p>
        <div className="flex justify-center gap-3">
          <button
            className="btn-primary px-5 py-2.5"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a className="btn-accent px-5 py-2.5" href="/">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
